import React, { useEffect, useState } from 'react';
import './MacroIssuesTable.css';

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function tradeLabel(sides) {
  if (!Array.isArray(sides) || sides.length === 0) return '—';
  return sides
    .map((side) => `${side.player} ${side.total}`)
    .join(' · ');
}

/**
 * Key Battles card — fights ranked by cost from /api/replay_facts/…/battles.
 * All players; no browser derivation.
 */
const KeyBattlesCard = ({ apiBaseUrl, replayId, top = 5, onSeek }) => {
  const [state, setState] = useState({ status: 'idle', battles: [], error: '' });

  useEffect(() => {
    if (!apiBaseUrl || !replayId) {
      setState({ status: 'empty', battles: [], error: '' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', battles: [], error: '' });
    const url =
      `${apiBaseUrl}/replay_facts/${encodeURIComponent(replayId)}` +
      `/battles?top=${top}`;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const battles = Array.isArray(data) ? data : [];
        setState({
          status: battles.length === 0 ? 'empty' : 'ready',
          battles,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          battles: [],
          error: err?.message || 'Request failed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replayId, top]);

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="macro-issues" data-testid="key-battles-body">
        <p className="macro-issues-empty">Loading Key Battles…</p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="macro-issues" data-testid="key-battles-body">
        <p className="macro-issues-empty" data-testid="key-battles-error">
          Could not load Key Battles ({state.error}).
        </p>
      </div>
    );
  }
  if (state.status === 'empty') {
    return (
      <div className="macro-issues" data-testid="key-battles-body">
        <p className="macro-issues-empty">No battles with army losses.</p>
      </div>
    );
  }

  return (
    <div className="macro-issues" data-testid="key-battles-body">
      <table className="macro-issues-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Cost</th>
            <th>Trade</th>
          </tr>
        </thead>
        <tbody>
          {state.battles.map((battle, index) => (
            <tr
              key={`${battle.start}-${index}`}
              className="macro-issues-row"
              data-testid="key-battles-row"
              onClick={() => onSeek?.(battle.start)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSeek?.(battle.start);
                }
              }}
              tabIndex={0}
              role="button"
            >
              <td>
                <span className="macro-issues-range">
                  {formatClock(battle.start)}–{formatClock(battle.end)}
                </span>
              </td>
              <td>
                <span className="macro-issues-dur">{battle.total}</span>
              </td>
              <td>{tradeLabel(battle.sides)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="macro-issues-hint">Click a row to seek the Playhead and pause.</p>
    </div>
  );
};

export default KeyBattlesCard;
